<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            v-if="rowData"
            :column-defs="columnDefs"
            :row-data="rowData"
            :is-auto-width="true"
            :saveRowOrder="saveRowOrder"
            :enableCellTextSelection="false"
        >
            <responsive-button
                :icon="['fas', 'arrows-rotate']"
                @click="fetchData"
                variant="primary"
                >Refresh</responsive-button
            >
            <responsive-button
                variant="primary"
                title="Add New Trigger"
                @click="handleAddClick"
                :icon="['fas', 'plus']"
            >
                Add New Trigger
            </responsive-button>
        </grid>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';

import API from '../../api';
import Page from '../generic/page/page.vue';
import Grid from '../generic/grid/grid.vue';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
import { formatResponseError } from '../../utils/api';
import { TRIGGER_ENDPOINT } from '../../endpoints';

export default {
    name: 'TriggerList',
    components: {
        Page,
        Grid,
        ResponsiveButton,
        actionsCellRenderer: ActionsCellRenderer,
    },
    mixins: [removeScrollMixin],
    data() {
        return {
            title: 'Triggers',
            tabs: [
                { title: 'Triggers', route: '/app/trigger/list', active: true },
            ],
            busy: false,
            rowData: null,
        };
    },
    mounted() {
        this.fetchData();
    },
    computed: {
        ...mapGetters('header', ['userHasCustomSMSGateway']),
        columnDefs() {
            return [
                {
                    field: 'order',
                    headerName: 'Order',
                    cellClass: 'first-drag-column',
                    sortIndex: 4,
                    sort: 'asc',
                    minWidth: 100,
                    rowDrag: true,
                    rowDragText: ({ defaultTextValue, rowNode }) =>
                        `${defaultTextValue} ${rowNode.data.title}`,
                },
                {
                    field: 'id',
                    headerName: 'ID',
                    rowDrag: this.isLargeScreen,
                    rowDragText: ({ rowNode }) =>
                        `${rowNode.data.order} ${rowNode.data.title}`,
                },
                {
                    field: 'title',
                    headerName: 'Title',
                    rowDrag: this.isLargeScreen,
                    rowDragText: ({ defaultTextValue, rowNode }) =>
                        `${rowNode.data.order} ${defaultTextValue}`,
                },
                {
                    field: 'created',
                    headerName: 'Created',
                    type: 'dateColumn',
                    rowDrag: this.isLargeScreen,
                    rowDragText: ({ rowNode }) =>
                        `${rowNode.data.order} ${rowNode.data.title}`,
                },
                {
                    field: 'modified',
                    headerName: 'Modified',
                    type: 'dateColumn',
                    rowDrag: this.isLargeScreen,
                    rowDragText: ({ rowNode }) =>
                        `${rowNode.data.order} ${rowNode.data.title}`,
                },
                {
                    field: 'creator',
                    headerName: 'Creator',
                    rowDrag: this.isLargeScreen,
                    rowDragText: ({ rowNode }) =>
                        `${rowNode.data.order} ${rowNode.data.title}`,
                },
                {
                    field: 'has_push_notification',
                    headerName: 'Push',
                    valueGetter: (params) => (params.data.has_push_notification ? 'X' : ''),
                    sortIndex: 1,
                    sort: 'desc',
                    minWidth: 95,
                    rowDrag: this.isLargeScreen,
                    rowDragText: ({ rowNode }) =>
                        `${rowNode.data.order} ${rowNode.data.title}`,
                },                
                {
                    field: 'has_sms',
                    headerName: 'SMS',
                    valueGetter: (params) => (params.data.has_sms ? 'X' : ''),
                    sortIndex: 2,
                    sort: 'desc',
                    minWidth: 95,
                    rowDrag: this.isLargeScreen,
                    rowDragText: ({ rowNode }) =>
                        `${rowNode.data.order} ${rowNode.data.title}`,
                },
                {
                    field: 'has_email',
                    headerName: 'Email',
                    valueGetter: (params) => (params.data.has_email ? 'X' : ''),
                    sortIndex: 3,
                    sort: 'desc',
                    minWidth: 95,
                    rowDrag: this.isLargeScreen,
                    rowDragText: ({ rowNode }) =>
                        `${rowNode.data.order} ${rowNode.data.title}`,
                },
                {
                    field: 'issues',
                    headerName: 'Warnings',
                    cellRenderer: ({ data }) => {
                        if (data.issues.length > 0)
                            return `<span class="text-danger">${data.issues.join(
                                '<br>'
                            )}<span>`;
                        return 'N/A';
                    },
                    rowDrag: this.isLargeScreen,
                    rowDragText: ({ rowNode }) =>
                        `${rowNode.data.order} ${rowNode.data.title}`,
                },
                {
                    field: 'enabled',
                    headerName: 'Enabled',
                    type: 'switchColumn',
                    resizable: false,
                    sortIndex: 0,
                    sort: 'desc',
                    autoWidthExclude: true,
                    valueGetter: ({ data }) => {
                        return data.is_enabled || data.enabled;
                    },
                    cellRendererParams: {
                        onChange: async ({ data, node, api }, value) => {
                            try {
                                await API.post(
                                    `${TRIGGER_ENDPOINT(
                                        data.id
                                    )}set-enable/`,
                                    { is_enabled: value }
                                );
                            } catch (e) {
                                this.errorToast({
                                    text: `Could not ${
                                        !!value ? 'enable' : 'disable'
                                    } trigger`,
                                });
                                api.redrawRows({
                                    rowNodes: [node],
                                });
                                return;
                            }
                        },
                    },
                    rowDrag: this.isLargeScreen,
                    rowDragText: ({ rowNode }) =>
                        `${rowNode.data.order} ${rowNode.data.title}`,
                },
                {
                    headerName: '',
                    maxWidth: 130,
                    minWidth: 130,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'right' },
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        delete: async (data) => {
                            this.busy = true;
                            try {
                                await API.delete(TRIGGER_ENDPOINT(data.id));
                                await this.fetchData();
                                this.successToast({
                                    text: `Trigger successfully deleted.`,
                                });
                            } catch (err) {
                                const { generic } = formatResponseError(err);
                                this.errorToast({
                                    text:
                                        generic || 'Could not delete trigger.',
                                });
                            } finally {
                                this.busy = false;
                            }
                        },
                        reset: async (data) => {
                            this.busy = true;
                            try {
                                await API.post(
                                    `${TRIGGER_ENDPOINT(
                                        data.id
                                    )}reset/`
                                );
                                await this.fetchData();
                                this.successToast({
                                    text: `Trigger successfully reset.`,
                                });
                            } catch (err) {
                                const { generic } = formatResponseError(err);
                                this.errorToast({
                                    text:
                                        generic || 'Could not reset trigger.',
                                });
                            } finally {
                                this.busy = false;
                            }
                        },                        
                    },
                },
            ];
        },
        isLargeScreen() {
            const md = 1920;
            return window.innerWidth > md;
        },
    },
    methods: {
        async fetchData() {
            try {
                this.busy = true;
                const { data } = await API.get(TRIGGER_ENDPOINT());
                this.rowData = data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch triggers.' });
            } finally {
                this.busy = false;
            }
        },
        async saveRowOrder({ row, order, onSuccess, onError }) {
            try {
                onSuccess();
                await API.post(`${TRIGGER_ENDPOINT(row.id)}set-order/`, {
                    order,
                });
                await this.fetchData();
            } catch (e) {
                this.errorToast({ text: 'Could not reorder triggers.' });
            }
        },
        handleAddClick() {
            this.$store.dispatch('trigger/clearTriggerData', this.userHasCustomSMSGateway);
            this.$router.push(`/app/trigger/0/edit/`);
        },
    },
};
</script>

<style scoped lang="scss">
:deep(.ag-icon-grip) {
    position: absolute;
    pointer-events: auto;
    top: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
}
:deep(.ag-drag-handle.ag-row-drag) {
    margin-right: 0px;
}
:deep(.first-drag-column .ag-drag-handle.ag-row-drag) {
    margin-right: 12px;
}
:deep(.first-drag-column .ag-icon-grip) {
    position: relative;
    top: 12px;
    opacity: 1;
}
:deep(.ag-header-cell-label .ag-header-icon.ag-sort-order) {
  display: none
}
</style>
